import { useState } from "react";
import { DownArrow, DeleteSVG } from "../svg";
import useNoInitialRenderEffect from "../../hooks/useNoInitialRenderEffect";
import Linebreak from "../linebreak";
import BoxWithInput from "./box-with-input";
import { CustomQuoteCopy } from "../custom-quote-copy";
import { getPackingCharge } from "@/lib/calc-packing";
import OverlayPopup from "../overlay-popup";
import {
  MIN_ITEM_DESCRIPTION_LENGTH,
  MAX_ITEM_DESCRIPTION_LENGTH,
} from "@/lib/constants";
import ItemList from "@/components/create-shipment-form/item-list";

export default function Package({
  packageIndex,
  parcel,
  updatePackage,
  itemsList,
  formData,
  itemValueUpdateCounter,
  setItemValueUpdateCounter,
  userBoxes,
  setIsError,
}) {
  const [packingType, setPackingType] = useState(parcel.packingType ?? "");
  const [showCustomQuote, setShowCustomQuote] = useState(false);
  const [declaredValue, setDeclaredValue] = useState(0);
  const { length, width, height, weight, validPackingType } = parcel;

  const [toggle, setToggle] = useState(false);
  const [isPackageFilled, setIsPackageFilled] = useState("none");
  const [alert, setAlert] = useState(false);
  const [animateDelete, setAnimateDelete] = useState(false);

  const calcPackingCosts = async (width, length, height) => {
    const { regularPackingCost, fragilePackingCost } = getPackingCharge({
      length,
      width,
      height,
    });
    const pkgDetails = {
      regularPackingCost: regularPackingCost ?? 0,
      fragilePackingCost: fragilePackingCost ?? 0,
      length,
      width,
      height,
      weight,
      volumetricWeight: (length * width * height) / 5000 ?? 0,
    };
    updatePackage("package", pkgDetails, packageIndex);
  };

  const checkIfOverweight = (width, length, height, weight) => {
    const volumetricWeight = (width * length * height) / 5000;
    if (volumetricWeight !== NaN && volumetricWeight !== 0 && weight) {
      const chargeableWeight = Math.max(volumetricWeight, weight);
      if (chargeableWeight <= 497) {
        setShowCustomQuote(false);
        setIsError(false);
      } else {
        setShowCustomQuote(true);
        setIsError(true);
      }
    }
  };

  const checkPackageFilled = () => {
    let filled = true;
    const emptyPkgFields = {};
    if (!length) emptyPkgFields.validLength = false;
    if (!width) emptyPkgFields.validWidth = false;
    if (!height) emptyPkgFields.validHeight = false;
    if (!weight) emptyPkgFields.validWeight = false;
    updatePackage("package", emptyPkgFields, packageIndex);

    for (let i = 0; i < itemsList.length; i++) {
      const { quantity, description, category, totalValue, totalWeight } =
        itemsList[i];
      const emptyItemFields = {};
      if (!quantity) emptyItemFields.validQuantity = false;

      if (
        description &&
        !(
          description.trim().length >= MIN_ITEM_DESCRIPTION_LENGTH &&
          description.trim().length <= MAX_ITEM_DESCRIPTION_LENGTH
        )
      ) {
        emptyItemFields.validDescription = false;
      }
      if (!category) emptyItemFields.validCategory = false;
      if (!totalValue) emptyItemFields.validTotalValue = false;
      if (!totalWeight) emptyItemFields.validTotalWeight = false;
      updatePackage("item", emptyItemFields, packageIndex, i);
      if (Object.keys(emptyItemFields).length > 0) filled = false;
    }
    if (Object.keys(emptyPkgFields).length > 0) filled = false;
    setIsPackageFilled(filled);
    return filled;
  };

  useNoInitialRenderEffect(() => {
    checkIfOverweight(width, length, height, weight);
    calcPackingCosts(width, length, height);
  }, [width, length, height, weight]);

  //get total declared value
  useNoInitialRenderEffect(() => {
    let total = 0;
    itemsList.forEach((item) => {
      total += item.totalValue ? parseFloat(item.totalValue) : 0;
    });
    setDeclaredValue(total);
    updatePackage("declaredValue", total, packageIndex);
  }, [itemValueUpdateCounter]);

  const packageResizeAnimation = () => {
    const packageContents = document.getElementById(
      `main-content-p${packageIndex}`
    );
    const minimisedPackage = document.getElementById(
      `p${packageIndex}-minimized`
    );
    if (toggle) {
      packageContents.style.display = "block";
      packageContents.style.maxHeight = packageContents.scrollHeight + "px";
      packageContents.style.overflow = "visible";
      minimisedPackage.style.height = 0 + "px";
      setToggle(!toggle);
      setTimeout(() => {
        packageContents.style.removeProperty("max-height");
      }, 500);
    } else {
      packageContents.style.maxHeight = packageContents.scrollHeight + "px";
      // setAnimationStart(!animationStart);
      setTimeout(() => {
        packageContents.style.maxHeight =
          minimisedPackage.scrollHeight + 10 + "px";
        packageContents.style.overflow = "hidden";
      }, 10);
      setTimeout(() => {
        minimisedPackage.style.height = minimisedPackage.scrollHeight + "px";
        packageContents.style.display = "none";
        setToggle(!toggle);
      }, 400);
    }
  };

  useNoInitialRenderEffect(() => {
    updatePackage("validPackingType", true, packageIndex);
    calcPackingCosts(width, length, height);
  }, [packingType]);

  return (
    <>
      {alert && (
        <OverlayPopup
          title="Are you sure you want to delete this package?"
          noText="Cancel"
          yesText="Confirm"
          actionOnYes={() => {
            setAnimateDelete(true);
            setAlert(false);
            setTimeout(() => {
              updatePackage("removePackage", null, packageIndex);
            }, 500);
          }}
          actionOnNo={() => setAlert(false)}
          useEnter={true}
          isAlert={true}
        >
          All your input details would be lost.
        </OverlayPopup>
      )}
      <div
        id={"package-" + packageIndex}
        className={`
         bg-white w-full md:rounded-xl overflow-clip animate-delete shadow ${
           animateDelete
             ? "max-h-[0px] mb-0 overflow-hidden"
             : "max-h-[calc(100%)]"
         } mb-8 scroll-mt-24 ${toggle ? "pb-4" : ""}`}
      >
        <div
          className={`flex justify-between hover:cursor-pointer`}
          onClick={(e) => {
            if (e.target.id !== "delete-svg") {
              if (checkPackageFilled()) packageResizeAnimation();
            }
          }}
        >
          <p className="text-dark-purple font-bold text-[22px] mt-6 ml-8 whitespace-nowrap">
            Package {packageIndex + 1}
          </p>
          <div className="flex items-center pt-4 mr-6 gap-x-4">
            <div
              className={`place-self-center  rounded-full w-[26px] h-[26px] flex items-center justify-center hover:cursor-pointer ${
                formData.packages.length > 1 ? "" : "hidden"
              }`}
              onClick={() => {
                setAlert(true);
              }}
            >
              <DeleteSVG />
            </div>

            <span>
              <DownArrow
                className={`w-[18px] h-[10px] transition-transform duration-300 ease-in-out ${
                  toggle ? "" : "-rotate-180"
                }`}
              />
            </span>
          </div>
        </div>
        <div
          id={`p${packageIndex}-minimized`}
          className={`${
            toggle
              ? "ml-8 mt-8 opacity-100 overflow-visible"
              : "opacity-0 max-h-0 overflow-hidden"
          } transition-opacity duration-300 ease-in-out font-semibold text-base`}
        >
          {packingType === "Fragile" ? (
            <p className="">Need Fragile Packing</p>
          ) : null}
          <p className="my-2">{`${length ?? ""} x ${width ?? ""} x ${
            height ?? ""
          } cm`}</p>
        </div>
        <div
          id={`main-content-p${packageIndex}`}
          className={` transition-all duration-500 ease-in-out  ${
            !toggle ? " opacity-100" : " opacity-0"
          }`}
        >
          <p
            className={`${
              isPackageFilled === false ? "text-error-red h-4" : "invisible"
            }  mt-6 md:mt-2  text-center`}
          >
            Please ensure all fields are filled to minimize
          </p>

          <p
            className={` ${
              validPackingType === false ? "text-error-red h-4" : "hidden"
            }  mt-4 md:md-2  text-center`}
          >
            Please select a packing type
          </p>
          <div
            className={`relative flex flex-col md:flex-row md:justify-evenly px-8 md:px-0 mt-4 mb-10 md:mb-6 gap-y-5  text-[15px]`}
          >
            <button
              type="button"
              id={`no-packing-p${packageIndex}`}
              className={`form-input w-full md:w-[28%] h-[55px]  ${
                packingType !== "No"
                  ? "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
                  : "font-semibold bg-light-purple text-white border-transparent"
              } `}
              onClick={() => {
                setPackingType("No");
                updatePackage("packingType", "No", packageIndex);
              }}
            >
              No Need Packing
            </button>
            <button
              type="button"
              id={`need-packing-p${packageIndex}`}
              className={`form-input w-full md:w-[28%] h-[55px]  ${
                packingType !== "Need"
                  ? "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
                  : "font-semibold bg-light-purple text-white border-transparent"
              }  `}
              onClick={() => {
                setPackingType("Need");
                updatePackage("packingType", "Need", packageIndex);
              }}
            >
              Need Packing
            </button>
            <button
              type="button"
              id={`fragile-packing-p${packageIndex}`}
              className={`form-input w-full md:w-[28%] h-[55px] ${
                packingType !== "Fragile"
                  ? "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
                  : "font-semibold bg-light-purple text-white border-transparent"
              } `}
              onClick={() => {
                setPackingType("Fragile");
                updatePackage("packingType", "Fragile", packageIndex);
              }}
            >
              Need Fragile Packing
            </button>
          </div>

          <BoxWithInput
            packingType={packingType}
            packageIndex={packageIndex}
            parcel={parcel}
            length={length}
            width={width}
            height={height}
            weight={weight}
            updatePackage={updatePackage}
            userBoxes={userBoxes}
          />
          <ItemList
            itemsList={itemsList}
            parcel={parcel}
            packageIndex={packageIndex}
            updatePackage={updatePackage}
            packingType={packingType}
            setItemValueUpdateCounter={setItemValueUpdateCounter}
          />

          <Linebreak />
          <div className="grid grid-cols-[78%_22%] items-center font-semibold px-8 pb-8">
            <p className="text-dark-purple ">
              Total declared <br className="md:hidden" /> value for all items
            </p>
            <p className="md:text-right text-dark-purple md:mr-5">
              ${declaredValue.toFixed(2)}
            </p>
          </div>

          {showCustomQuote && (
            <>
              <Linebreak className="mt-0 mb-4" />
              <CustomQuoteCopy />
            </>
          )}
        </div>
      </div>
    </>
  );
}
